import React from 'react'
import { Button } from '@/components/ui/button'

interface DataSourceCardProps {
  sourceName: string
  selected: boolean
  locked: boolean
  onSelect: () => void
}

/**
 * DataSourceCard — Step 2 of the AI creation flow.
 * Single-select, 2-column grid. Locks after selection.
 * Shows integration name with a colored icon container.
 * Per brand guide Section 12, Component 2.
 */
export const DataSourceCard: React.FC<DataSourceCardProps> = ({
  sourceName,
  selected,
  locked,
  onSelect,
}) => {
  // Generate a consistent first-letter abbreviation for the icon
  const initial = sourceName.charAt(0).toUpperCase()

  return (
    <Button
      type="button"
      variant="ghost"
      className={`flex items-center gap-2.5 w-full h-auto text-left px-3.5 py-3 rounded-xl border transition-all duration-150
        ${selected
          ? 'border-accent bg-accent-background cursor-default pointer-events-none hover:bg-accent-background'
          : locked
            ? 'border-border bg-card cursor-default pointer-events-none hover:bg-card'
            : 'border-border bg-card hover:border-accent hover:bg-accent-background'
        }`}
      aria-pressed={selected}
      aria-disabled={locked}
      aria-label={sourceName}
      onClick={locked ? undefined : onSelect}
      disabled={locked && !selected}
    >
      <div
        className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
          selected ? 'bg-accent-background' : 'bg-muted'
        }`}
      >
        <span
          className={`text-xs font-bold ${selected ? 'text-accent' : 'text-muted-foreground'}`}
          aria-hidden="true"
        >
          {initial}
        </span>
      </div>
      <span className={`text-sm font-semibold ${selected ? 'text-accent' : 'text-primary'}`}>
        {sourceName}
      </span>
    </Button>
  )
}
